import { once } from 'lodash';
import { useRef, useMemo } from 'react';
/**
 * Returns a callback function that’s guaranteed to be referentially stable
 * (the same function instance) for the entire lifecycle of the component.
 * The returned function, when called, will always delegate to the latest
 * value of the underlying callback prop `cb`.
 *
 * This is mostly intended for situations in which the correct *behavior* of
 * a component depends on referential stability of a callback. In this situation
 * it’s too brittle to rely on all ancestor components to get referential
 * stability right.
 *
 * @example
 * const VideoPlayer: FC<{onReady: () => void}> = ({onReady}) => {
 *   const playerRef = useRef<VideoJsPlayer |  null>(null);
 *   const handleReady = useStabilizedCallback(onReady);
 *
 *   useEffect(() => {
 *     playerRef.current = new VideoJsPlayer({onReady: handleReady});
 *
 *    // We can safely declare handleReady as a dependency because
 *    // useStabilizedCallback guarantees referential stability. Even if onReady
 *    // changes, the effect will only execute once, and it will call the most
 *    // recent value of the onReady prop when the player is ready.
 *   }, [handleReady]);
 *
 *   return <div ref={playerRef} />
 * };
 */
export const useStabilizedCallback = (cb) => {
    const lastCbRef = useRef(cb);
    lastCbRef.current = cb;
    // This seems like it would be a good use case for useCallback(), but the
    // React docs make it clear that useMemo() is not guaranteed to hold on to
    // memoized values, and that “you may rely on useMemo as a performance
    // optimization, not as a semantic guarantee.”
    // https://reactjs.org/docs/hooks-reference.html#usememo
    // Since useCallback() is just a light abstraction on top of useMemo(), I
    // assume the same is true for it.
    const stabilizedCallbackRef = useRef((...args) => {
        const lastCb = lastCbRef.current;
        return lastCb(...args);
    });
    return stabilizedCallbackRef.current;
};
export const useOnce = (cb) => {
    const cbFunc = useStabilizedCallback(cb);
    return useMemo(() => once(cbFunc), [cbFunc]);
};
//# sourceMappingURL=useStabilizedCallback.preenvesnextjs.map
